import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Plus, Pencil, Trash2, MapPin, Warehouse } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper para verificar permisos
const userCan = (user, module, action) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (user.permissions) {
    return user.permissions[module]?.[action] === true;
  }
  return false;
};

function Warehouses() {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  
  // Permisos del usuario
  const canCreate = userCan(user, 'depositos', 'crear');
  const canEdit = !!user && user.role === 'admin';
  const canDelete = userCan(user, 'depositos', 'eliminar');
  
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [formData, setFormData] = useState({ name: '', location: '', description: '' });
  const [confirmDelete, setConfirmDelete] = useState(null); // warehouse object pending delete
  const [deleting, setDeleting] = useState(false);
  const [includeSubdepots, setIncludeSubdepots] = useState(false);
  
  useEffect(() => {
    fetchWarehouses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeSubdepots]);
  
  const fetchWarehouses = async () => {
    try {
      const url = `${BACKEND_URL}/api/warehouses?include_subdepots=${includeSubdepots ? 'true' : 'false'}`;
      const response = await axios.get(url, { withCredentials: true });
      setWarehouses(response.data);
    } catch (error) {
      toast.error('Error al cargar depósitos');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingWarehouse) {
        await axios.put(`${BACKEND_URL}/api/warehouses/${editingWarehouse.warehouse_id}`, formData, { withCredentials: true });
        toast.success('Depósito actualizado');
      } else {
        await axios.post(`${BACKEND_URL}/api/warehouses`, formData, { withCredentials: true });
        toast.success('Depósito creado');
      }
      setDialogOpen(false);
      setEditingWarehouse(null);
      setFormData({ name: '', location: '', description: '' });
      fetchWarehouses();
    } catch (error) {
      toast.error('Error al guardar depósito');
    }
  };
  
  const handleEdit = (warehouse) => {
    setEditingWarehouse(warehouse);
    setFormData({
      name: warehouse.name,
      location: warehouse.location,
      description: warehouse.description || ''
    });
    setDialogOpen(true);
  };
  
  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/warehouses/${confirmDelete.warehouse_id}`, { withCredentials: true });
      toast.success('Depósito eliminado');
      setConfirmDelete(null);
      fetchWarehouses();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al eliminar depósito';
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };
  
  const resetForm = () => {
    setEditingWarehouse(null);
    setFormData({ name: '', location: '', description: '' });
  };
  
  if (loading) {
    return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;
  }
  
  return (
    <div className="space-y-6" data-testid="warehouses-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2">Depósitos</h1>
          <p className="text-muted-foreground">Gestiona tus depósitos y ubicaciones</p>
        </div>
        <div className="flex items-center gap-3">
          {user?.role === 'admin' && (
            <label className="flex items-center gap-2 text-sm cursor-pointer" title="Mostrar depósitos que son subdepósitos de áreas internas">
              <input
                type="checkbox"
                checked={includeSubdepots}
                onChange={(e) => setIncludeSubdepots(e.target.checked)}
                className="h-4 w-4"
                data-testid="toggle-subdepots"
              />
              <span>Incluir subdepósitos de áreas</span>
            </label>
          )}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            {canCreate && (
            <DialogTrigger asChild>
              <Button data-testid="create-warehouse-button">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Depósito
              </Button>
            </DialogTrigger>
            )}
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingWarehouse ? 'Editar Depósito' : 'Nuevo Depósito'}</DialogTitle>
              <DialogDescription>Completa la información del depósito</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Nombre *</Label>
                <Input id="name" data-testid="warehouse-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required disabled={editingWarehouse?.is_area_subdepot} />
                {editingWarehouse?.is_area_subdepot && (
                  <p className="text-xs text-rose-600 mt-1">El nombre de un sub-depósito se cambia renombrando su Área (Pedidos Internos → Áreas).</p>
                )}
              </div>
              <div>
                <Label htmlFor="location">Ubicación *</Label>
                <Input id="location" data-testid="warehouse-location-input" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} required />
              </div>
              <div>
                <Label htmlFor="description">Descripción</Label>
                <Textarea id="description" data-testid="warehouse-description-input" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="warehouse-submit-button">{editingWarehouse ? 'Actualizar' : 'Crear'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {warehouses.map((warehouse) => (
          <Card
            key={warehouse.warehouse_id}
            data-testid="warehouse-card"
            className="hover:shadow-lg hover:border-primary/40 transition-all cursor-pointer"
            onClick={() => navigate(`/warehouses/${warehouse.warehouse_id}/stock`)}
          >
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-xl">{warehouse.name}</CardTitle>
                    {warehouse.is_area_subdepot && (
                      <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-300 text-xs" title={`Subdepósito del área: ${warehouse.parent_area_name || ''}`}>
                        Subdepósito
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center mt-2 text-sm text-muted-foreground">
                    <MapPin className="w-4 h-4 mr-1" />
                    {warehouse.location || '—'}
                  </div>
                  {warehouse.is_area_subdepot && warehouse.parent_area_name && (
                    <p className="text-xs text-rose-600 mt-1">Área: {warehouse.parent_area_name}</p>
                  )}
                  <p className="text-xs text-primary mt-2">Click para ver stock →</p>
                </div>
                {(canEdit || canDelete) && (
                <div className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
                  {canEdit && (
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(warehouse)} data-testid="edit-warehouse-button">
                      <Pencil className="w-4 h-4" />
                    </Button>
                  )}
                  {canDelete && (
                    <Button variant="ghost" size="icon" onClick={() => setConfirmDelete(warehouse)} data-testid="delete-warehouse-button">
                      <Trash2 className="w-4 h-4 text-rose-600" />
                    </Button>
                  )}
                </div>
                )}
              </div>
            </CardHeader>
            {warehouse.description && (
              <CardContent>
                <p className="text-sm text-muted-foreground">{warehouse.description}</p>
              </CardContent>
            )}
          </Card>
        ))}
      </div>
      
      {warehouses.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Warehouse className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No hay depósitos registrados. Crea uno para comenzar.</p>
          </CardContent>
        </Card>
      )}

      {/* Confirm delete dialog */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="confirm-delete-warehouse-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar depósito</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de eliminar el depósito <strong>{confirmDelete?.name}</strong>?
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-warehouse">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-rose-600 text-white hover:bg-rose-700"
              data-testid="confirm-delete-warehouse"
            >
              {deleting ? 'Eliminando…' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default Warehouses;